import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Bars3Icon,
  XMarkIcon,
  AcademicCapIcon,
  UserCircleIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  HeartIcon,
  ShoppingCartIcon,
  PencilIcon,
  ShieldCheckIcon,
  SparklesIcon,
  GlobeAltIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { SUPPORTED_COUNTRIES, resolveCountry } from '../../constants/countries';
import { useCart } from '../../context/CartContext';
import { authApi } from '../../services/api';
import { getErrorMessage } from '../../utils/errorUtils';
import { scrollToTop } from '../../utils/scrollUtils';
import toast from 'react-hot-toast';
import Coursess from '../assets/icons/Courses.svg'
import workflowicon from '../assets/icons/Workboard-2.svg'
import UserAvatar from '../ui/UserAvatar';
import NotificationBell from './NotificationBell';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, isAdmin, isSuperAdmin, isReviewer, updateUser, currentCountry, setCurrentCountry } = useAuth();
  const activeCountry = resolveCountry(currentCountry);
  const { getCartCount } = useCart();

  // Helper function to check if user is a candidate (end user)
  const isCandidate = () => {
    return isAuthenticated && (
      user?.active_role === 'candidate' ||
      user?.role === 'candidate' ||
      user?.active_role === 'student' ||
      user?.role === 'student'  // Legacy support
    );
  };




  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Cog6ToothIcon className="h-4 w-4" />;
      case 'creator':
        return <PencilIcon className="h-4 w-4" />;
      case 'reviewer':
        return <ShieldCheckIcon className="h-4 w-4" />;
      case 'approver':
        return <SparklesIcon className="h-4 w-4" />;
      case 'candidate':
      case 'student':
        return <AcademicCapIcon className="h-4 w-4" />;
      default:
        return <UserCircleIcon className="h-4 w-4" />;
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'creator':
        return 'Creator';
      case 'reviewer':
        return 'Reviewer';
      case 'approver':
        return 'Approver';
      case 'candidate':
        return 'Student';
      case 'student':
        return 'Student';
      default:
        return role?.charAt(0).toUpperCase() + role?.slice(1) || 'User';
    }
  };

  const getRoleColorClass = (role) => {
    switch (role) {
      case 'admin':
        return 'text-red-600 bg-red-50';
      case 'creator':
        return 'text-blue-600 bg-blue-50';
      case 'reviewer':
        return 'text-green-600 bg-green-50';
      case 'approver':
        return 'text-purple-600 bg-purple-50';
      case 'candidate':
      case 'student':
        return 'text-gray-600 bg-gray-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };



  const getRoleDescription = (role) => {
    switch (role) {
      case 'admin':
        return 'Manage users and system settings';
      case 'creator':
        return 'Create and manage courses';
      case 'reviewer':
        return 'Review and approve courses';
      case 'approver':
        return 'Final approval and publishing';
      case 'candidate':
      case 'student':
        return 'Access learning content';
      default:
        return 'User access';
    }
  };

  const handleRoleSwitch = async (newRole) => {
    if (newRole === user?.active_role || switching) {
      return;
    }

    try {
      setSwitching(true);

      const response = await authApi.switchRole({
        new_active_role: newRole
      });

      if (response.data.access_token) {
        // Update token in cookies
        const cookies = await import('js-cookie');
        cookies.default.set('authToken', response.data.access_token, { expires: 1 });

        // Update user context
        updateUser(response.data.user);

        toast.success(`Switched to ${getRoleLabel(newRole)} role`);
        setShowUserMenu(false);
        setShowRoleDropdown(false);

        // Navigate to the appropriate dashboard for the new role
        const dashboardPaths = {
          'admin': '/super-admin',
          'approver': '/approver',
          'reviewer': '/reviewer',
          'creator': '/admin',
          'candidate': '/dashboard',
          'student': '/dashboard'  // Legacy role
        };

        const targetPath = dashboardPaths[newRole] || '/dashboard';

        // Navigate to the correct dashboard for the new role
        // The key-based remounting in App.js will handle re-rendering the same path
        navigate(targetPath);

        // Scroll to top after navigation
        setTimeout(() => scrollToTop(), 100);
      }
    } catch (error) {
      console.error('Error switching role:', error);
      toast.error(getErrorMessage(error, 'Failed to switch role'));
    } finally {
      setSwitching(false);
    }
  };

  const getNavigation = () => {
    if (isAuthenticated) {
      // Check active role for navigation
      const activeRole = user?.active_role || user?.role;

      if (activeRole === 'admin') {
        // Admin users see Workflow Management and Admin Dashboard
        return [
          { name: <img style={{ height: 28 }} src={workflowicon}></img>, href: '/super-admin/courses', title: 'Workbench' },
          // { name: 'Dashboard', href: '/super-admin' },
          // { name: 'Analytics', href: '/super-admin/analytics' },
        ];
      } else if (activeRole === 'approver') {
        // Approver users see Courses and Approver Dashboard
        return [
          // { name: 'Courses', href: '/courses' },
          // { name: 'Dashboard', href: '/approver' },
        ];
      } else if (activeRole === 'reviewer') {
        // Reviewer users see Courses and Reviewer Dashboard
        return [
          // { name: 'Courses', href: '/courses' },
          // { name: 'Dashboard', href: '/reviewer' },
        ];
      } else if (activeRole === 'creator') {
        // Creator users see Courses and Creator Dashboard
        return [
          // { name: 'Courses', href: '/courses' },
          // { name: 'Dashboard', href: '/admin' },
        ];
      } else {
        // Candidate users see navigation without Home + Dashboard + Wishlist + Cart
        return [
          // { name: 'Courses', href: '/courses' },
          { name: <img style={{ height: 25 }} src={Coursess} alt="Courses" />, href: '/courses', title: 'Courses' },

          // { name: 'About', href: '/about' },
          // { name: 'Features', href: '/features' },

        ];
      }
    }

    // Non-authenticated users see basic navigation
    return [
      { name: 'Courses', href: '/courses' },
      // {name: <img src={Coursess} alt="Courses" />, href: '/courses'},

    ];
  };

  const navigation = getNavigation();

  const isActive = (href) => {
    if (href.startsWith('#')) {
      return false; // Handle section links differently
    }
    return location.pathname === href;
  };

  const handleSectionLink = (href) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    setShowUserMenu(false);
    navigate('/');
    // localStorage.removeItem("unify_cart");
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleRegister = () => {
    navigate('/register');
  };

  return (
    <nav
      className={`gradient-bg shadow-lg ${location.pathname === "/" ? "absolute w-full top-0 z-50" : "gradient-bg shadow-lg sticky top-0 left-0 right-0 z-50"
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">

            <Link
              to={
                isAuthenticated && user ? (
                  user?.active_role === 'admin' ? "/super-admin" :
                    user?.active_role === 'approver' ? "/approver" :
                      user?.active_role === 'reviewer' ? "/reviewer" :
                        user?.active_role === 'creator' ? "/admin" :
                          "/dashboard"
                ) : "/"
              }
              className="flex items-center space-x-2"
            >
              <AcademicCapIcon className="h-8 w-8 text-white" />
              <span className="text-xl font-bold text-white">Unify Academy</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => handleSectionLink(item.href)}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 ${isActive(item.href)
                  ? 'text-white bg-white/20 backdrop-blur-sm'
                  : 'text-white/90 hover:text-white hover:bg-white/10'
                  }`}
              >
                {item.name}
              </Link>
            ))}

            {/* Wishlist and Cart for authenticated candidates */}
            {isCandidate() && (
              <div className="flex items-center space-x-4">
                <Link
                  to="/wishlist"
                  className={`p-2 rounded-lg transition-colors ${isActive('/wishlist')
                    ? 'text-white bg-white/20 backdrop-blur-sm'
                    : 'text-white/90 hover:text-white hover:bg-white/10 hover:backdrop-blur-sm'
                    }`}
                >
                  <HeartIcon className="h-6 w-6" />
                </Link>
                <Link
                  to="/cart"
                  className={`p-2 rounded-lg transition-colors relative ${isActive('/cart')
                    ? 'text-white bg-white/20 backdrop-blur-sm'
                    : 'text-white/90 hover:text-white hover:bg-white/10 hover:backdrop-blur-sm'
                    }`}
                >
                  <ShoppingCartIcon className="h-6 w-6" />
                  {getCartCount() > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {getCartCount()}
                    </span>
                  )}
                </Link>
              </div>
            )}

            {/* Authentication Controls */}
            {isAuthenticated ? (
              <div className="flex items-center gap-4">
                {/* Deadline bell. It renders only while the user is actually
                    working as a reviewer or an approver - holding the role is
                    not enough - so it decides for itself rather than being
                    gated on isReviewer() / isApprover(), which are true for
                    every role an account holds. */}
                <NotificationBell />

                {/* Integrated Profile Menu with Role Switching */}
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-2 text-white/90 hover:text-white transition-colors"
                  >
                    <div className="h-8 w-8 rounded-full overflow-hidden border border-white/20">
                      <UserAvatar user={user} variant="small" />
                    </div>
                    {/* <span className="text-sm font-medium">{user?.full_name}</span> */}
                    {/* <ChevronDownIcon className="h-4 w-4" /> */}
                  </button>

                  <AnimatePresence>
                    {showUserMenu && (
                      <>
                        {/* Backdrop */}
                        <div
                          className="fixed inset-0 z-40"
                          onClick={() => {
                            setShowUserMenu(false);
                            setShowRoleDropdown(false);
                            setShowCountryDropdown(false);
                          }}
                        />

                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-50"
                        >
                          {/* Profile Header - the compact region selector on the
                              right is shown ONLY when the active role is student. */}
                          <UserAvatar
                            user={user}
                            rightSlot={
                              (user?.active_role || user?.role) === 'candidate' ||
                              (user?.active_role || user?.role) === 'student' ? (
                              <div className="relative">
                                <button
                                  onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                                  title="Region"
                                  className="flex items-center gap-1 pl-1.5 pr-1 py-1 rounded-md border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                                >
                                  <GlobeAltIcon className="h-3.5 w-3.5 text-gray-400" />
                                  {activeCountry.code}
                                  <ChevronDownIcon
                                    className={`h-3 w-3 text-gray-400 transition-transform ${showCountryDropdown ? 'rotate-180' : ''
                                      }`}
                                  />
                                </button>

                                <AnimatePresence>
                                  {showCountryDropdown && (
                                    <motion.div
                                      initial={{ opacity: 0, y: -4, scale: 0.96 }}
                                      animate={{ opacity: 1, y: 0, scale: 1 }}
                                      exit={{ opacity: 0, y: -4, scale: 0.96 }}
                                      className="absolute top-full right-0 mt-1 w-20 bg-white border border-gray-200 rounded-md shadow-lg z-20 overflow-hidden py-1"
                                    >
                                      {SUPPORTED_COUNTRIES.map((c) => {
                                        const selected = c.code === activeCountry.code;
                                        return (
                                          <button
                                            key={c.code}
                                            onClick={() => {
                                              if (!selected) setCurrentCountry(c.code);
                                              setShowCountryDropdown(false);
                                            }}
                                            className={`w-full flex items-center justify-between px-2.5 py-1.5 text-left text-xs font-semibold transition-colors ${selected ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
                                              }`}
                                          >
                                            {c.code}
                                            {selected && (
                                              <CheckIcon className="h-3.5 w-3.5 text-blue-600" />
                                            )}
                                          </button>
                                        );
                                      })}
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                              ) : null
                            }
                          />



                          {/* Role Switching Section */}
                          {user?.roles && user.roles.length > 1 && (
                            <div className="p-3 border-b border-gray-100">
                              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                                Switch Role
                              </div>
                              <div className="relative">
                                <button
                                  onClick={() => setShowRoleDropdown(!showRoleDropdown)}
                                  disabled={switching}
                                  className={`w-full flex items-center gap-3 px-3 py-2 text-left rounded-md border transition-colors ${showRoleDropdown
                                    ? 'border-blue-300 bg-blue-50'
                                    : 'border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                  <div className={`p-1.5 rounded-md ${getRoleColorClass(user.active_role)}`}>
                                    {getRoleIcon(user.active_role)}
                                  </div>
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">
                                      {getRoleLabel(user.active_role)}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {switching ? 'Switching...' : 'Current role'}
                                    </div>
                                  </div>
                                  <ChevronDownIcon
                                    className={`h-4 w-4 text-gray-400 transition-transform ${showRoleDropdown ? 'rotate-180' : ''
                                      }`}
                                  />
                                </button>

                                <AnimatePresence>
                                  {showRoleDropdown && (
                                    <motion.div
                                      initial={{ opacity: 0, y: -5, scale: 0.95 }}
                                      animate={{ opacity: 1, y: 0, scale: 1 }}
                                      exit={{ opacity: 0, y: -5, scale: 0.95 }}
                                      className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10"
                                    >
                                      {Array.from(new Set(user.roles))
                                        .filter(role => role !== user.active_role)
                                        .filter(role => {
                                          // If user has both 'candidate' and 'student' roles, only show 'candidate'
                                          if (role === 'student' && user.roles.includes('candidate')) {
                                            return false;
                                          }
                                          return true;
                                        })
                                        .map((role) => (
                                          <button
                                            key={role}
                                            onClick={() => {
                                              handleRoleSwitch(role);
                                              setShowRoleDropdown(false);
                                            }}
                                            disabled={switching}
                                            className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 transition-colors first:rounded-t-md last:rounded-b-md"
                                          >
                                            <div className={`p-1.5 rounded-md ${getRoleColorClass(role)}`}>
                                              {getRoleIcon(role)}
                                            </div>
                                            <div className="flex-1">
                                              <div className="font-medium text-gray-900">
                                                {getRoleLabel(role)}
                                              </div>
                                              <div className="text-xs text-gray-500">
                                                {getRoleDescription(role)}
                                              </div>
                                            </div>
                                          </button>
                                        ))}
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            </div>
                          )}

                          {/* Navigation Links */}
                          <div className="py-2">
                            {/* <Link
                              to={
                                user?.active_role === 'admin' ? "/super-admin" :
                                  user?.active_role === 'approver' ? "/approver" :
                                    user?.active_role === 'reviewer' ? "/reviewer" :
                                      user?.active_role === 'creator' ? "/admin" :
                                        "/dashboard"
                              }
                              onClick={() => {
                                setShowUserMenu(false);
                                setTimeout(() => scrollToTop(), 50);
                              }}
                              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            >
                              <Cog6ToothIcon className="h-4 w-4 mr-3" />
                              Dashboard
                            </Link> */}

                            {isCandidate() && (
                              <>
                                <Link
                                  to="/account"
                                  onClick={() => {
                                    setShowUserMenu(false);
                                    setTimeout(() => scrollToTop(), 50);
                                  }}
                                  className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                >
                                  <UserCircleIcon className="h-4 w-4 mr-3" />
                                  Accounts
                                </Link>
                              </>
                            )}





                            <div className=" border-gray-100">
                              <button
                                onClick={handleLogout}
                                className="flex items-center w-full px-4 text-sm text-gray-700 hover:bg-gray-100"
                              >
                                <ArrowRightOnRectangleIcon className="h-4 w-4 mr-3" />
                                Sign out
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      </>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleLogin}
                  className="text-white/90 hover:text-white px-3 py-2 text-sm font-medium transition-colors"
                >
                  Sign in
                </button>
                {/* <button
                  onClick={handleRegister}
                  className="bg-white text-blue-600 hover:bg-gray-100 px-4 py-2 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105"
                >
                  Sign up
                </button> */}
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-white hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white transition-colors duration-300"
            >
              {isOpen ? (
                <XMarkIcon className="block h-6 w-6" />
              ) : (
                <Bars3Icon className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-white/10 backdrop-blur-lg"
          >
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {isAuthenticated ? (
                <>
                  {/* Authenticated Header Icons Row */}
                  <div className="flex flex-row items-center justify-around px-2 py-2 mb-2">
                    {navigation.map((item) => (
                      <Link
                        key={item.href} // key needs to be unique if name is complex element
                        to={item.href}
                        onClick={() => {
                          handleSectionLink(item.href);
                          setIsOpen(false);
                        }}
                        className={`p-2 rounded-md transition-all duration-300 ${isActive(item.href)
                          ? 'text-white bg-white/20'
                          : 'text-white/90 hover:text-white hover:bg-white/10'
                          }`}
                        title={item.title || "Link"}
                      >
                        {item.name}
                      </Link>
                    ))}

                    {/* Cart and Wishlist for Candidates - Mobile Icons Only */}
                    {isCandidate() && (
                      <>
                        <Link
                          to="/account"
                          onClick={() => {
                            setIsOpen(false);
                            setTimeout(() => scrollToTop(), 50);
                          }}
                          className={`p-2 rounded-md transition-all duration-300 ${isActive('/account')
                            ? 'text-white bg-white/20'
                            : 'text-white/90 hover:text-white hover:bg-white/10'
                            }`}
                        >
                          <UserCircleIcon className="h-6 w-6" />
                        </Link>
                        <Link
                          to="/wishlist"
                          onClick={() => {
                            setIsOpen(false);
                            setTimeout(() => scrollToTop(), 50);
                          }}
                          className={`p-2 rounded-md transition-all duration-300 ${isActive('/wishlist')
                            ? 'text-white bg-white/20'
                            : 'text-white/90 hover:text-white hover:bg-white/10'
                            }`}
                        >
                          <HeartIcon className="h-6 w-6" />
                        </Link>
                        <Link
                          to="/cart"
                          onClick={() => {
                            setIsOpen(false);
                            setTimeout(() => scrollToTop(), 50);
                          }}
                          className={`p-2 rounded-md transition-all duration-300 relative ${isActive('/cart')
                            ? 'text-white bg-white/20'
                            : 'text-white/90 hover:text-white hover:bg-white/10'
                            }`}
                        >
                          <ShoppingCartIcon className="h-6 w-6" />
                          {getCartCount() > 0 && (
                            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full h-4 w-4 flex items-center justify-center">
                              {getCartCount()}
                            </span>
                          )}
                        </Link>
                      </>
                    )}

                    {/* Dashboard Icon */}
                    <Link
                      to={isSuperAdmin() ? "/super-admin" : isReviewer() ? "/reviewer" : isAdmin() ? "/admin" : "/dashboard"}
                      onClick={() => {
                        setIsOpen(false);
                        setTimeout(() => scrollToTop(), 50);
                      }}
                      className="p-2 rounded-md text-white/90 hover:text-white hover:bg-white/10 transition-all duration-300"
                    >
                      <Cog6ToothIcon className="h-6 w-6" />
                    </Link>
                  </div>

                  <div className="px-3 py-2 border-t border-white/20 mt-2">
                    <p className="text-sm font-medium text-white">{user?.full_name}</p>
                    <p className="text-xs text-white/70 capitalize">{user?.role}</p>
                  </div>

                  {/* Mobile Role Switcher */}
                  {user?.roles && user.roles.length > 1 && (
                    <div className="px-3 py-3 border-t border-white/10">
                      <div className="text-xs font-semibold text-white/80 mb-2">Switch Role</div>
                      <div className="space-y-2">
                        {Array.from(new Set(user.roles))
                          .filter(role => role !== user.active_role)
                          .filter(role => {
                            if (role === 'student' && user.roles.includes('candidate')) return false;
                            return true;
                          })
                          .map(role => (
                            <button
                              key={role}
                              onClick={async () => {
                                await handleRoleSwitch(role);
                                setIsOpen(false); // close mobile menu after switching
                              }}
                              disabled={switching}
                              className="w-full flex items-center gap-3 px-3 py-2 text-left rounded-md bg-white/5 hover:bg-white/10 transition-colors"
                            >
                              <div className={`p-1.5 rounded-md ${getRoleColorClass(role)}`}>
                                {getRoleIcon(role)}
                              </div>
                              <div className="flex-1 text-left">
                                <div className="text-sm font-medium text-white">{getRoleLabel(role)}</div>
                                <div className="text-xs text-white/70">{getRoleDescription(role)}</div>
                              </div>
                            </button>
                          ))}
                      </div>
                    </div>
                  )}

                  <button
                    onClick={() => {
                      handleLogout();
                      setIsOpen(false);
                    }}
                    className="flex items-center w-full px-3 py-2 rounded-md text-base font-medium text-white/90 hover:text-white hover:bg-white/10 transition-all duration-300"
                  >
                    <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" />
                    Sign out
                  </button>
                </>
              ) : (
                /* Guest Navigation - Vertical List */
                <>
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => {
                        handleSectionLink(item.href);
                        setIsOpen(false);
                      }}
                      className={`block px-3 py-2 rounded-md text-base font-medium transition-all duration-300 ${isActive(item.href)
                        ? 'text-white bg-white/20'
                        : 'text-white/90 hover:text-white hover:bg-white/10'
                        }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                  <button
                    onClick={() => {
                      handleLogin();
                      setIsOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-white/90 hover:text-white hover:bg-white/10 transition-all duration-300"
                  >
                    Sign in
                  </button>
                  {/* <button
                    onClick={() => {
                      handleRegister();
                      setIsOpen(false);
                    }}
                    className="block w-full text-left px-3 py-2 rounded-md text-base font-medium bg-white text-blue-600 hover:bg-gray-100 transition-all duration-300"
                  >
                    Sign up
                  </button> */}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;